import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import Swal from "sweetalert2";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function saveSession(email) {
  localStorage.setItem("dsar_auth", "true");
  localStorage.setItem("dsar_user_type", "outside");
  localStorage.setItem("dsar_user_email", email);
}

// ─── DSARLogin Component ──────────────────────────────────────────────────────
const DSARLogin = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    const trimEmail = email.trim().toLowerCase();
    const trimPass = password.trim();

    if (!trimEmail || !trimPass) {
      const msg = "Please fill in all fields.";
      setError(msg);
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: msg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/user/dsar/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimEmail, password: trimPass })
      });

      let data = {};
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (jsonErr) {
        data = {};
      }

      if (!response.ok) {
        const errMsg = data.detail || data.message || data.error || (response.status === 401 ? "Invalid email or password." : `Login failed (Status: ${response.status}). Please try again.`);
        setError(errMsg);
        Swal.fire({
          icon: "error",
          title: response.status === 401 ? "Invalid Credentials" : "Login Failed",
          text: errMsg,
          customClass: { popup: "swal-popup-custom" }
        });
        setLoading(false);
        return;
      }

      saveSession(trimEmail);
      setSuccess("✓ Login successful!");
      Swal.fire({
        icon: "success",
        title: "Welcome!",
        text: "Login successful!",
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: "swal-popup-custom" }
      });
      onLoginSuccess?.({ type: "outside", email: trimEmail });
    } catch (err) {
      console.error("Login error:", err);
      const errMsg = (err && err.message && !err.message.includes("fetch")) ? err.message : "Unable to connect to the server. Please check your internet connection and try again.";
      setError(errMsg);
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: errMsg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    const trimEmail = email.trim().toLowerCase();
    const trimPass = password.trim();
    const trimConfirm = confirmPass.trim();

    if (!trimEmail || !trimPass || !trimConfirm) {
      const msg = "Please fill in all fields.";
      setError(msg);
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: msg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
      return;
    }
    if (trimPass.length < 6) {
      const msg = "Password must be at least 6 characters.";
      setError(msg);
      Swal.fire({
        icon: "warning",
        title: "Password Too Short",
        text: msg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
      return;
    }
    if (trimPass !== trimConfirm) {
      const msg = "Passwords do not match.";
      setError(msg);
      Swal.fire({
        icon: "warning",
        title: "Password Mismatch",
        text: msg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/user/dsar/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimEmail, password: trimPass })
      });

      let data = {};
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (jsonErr) {
        data = {};
      }

      if (!response.ok) {
        const errMsg = data.detail || data.message || data.error || (response.status === 400 ? "Email already registered." : `Registration failed (Status: ${response.status}). Please try again.`);
        setError(errMsg);
        const isDuplicate = response.status === 400 || (typeof errMsg === 'string' && (errMsg.toLowerCase().includes("already") || errMsg.toLowerCase().includes("exist") || errMsg.toLowerCase().includes("duplicate")));
        Swal.fire({
          icon: isDuplicate ? "warning" : "error",
          title: isDuplicate ? "Duplicate Account" : "Registration Failed",
          text: errMsg,
          customClass: { popup: "swal-popup-custom" }
        });
        setLoading(false);
        return;
      }

      setSuccess("✓ Registration successful! You can now log in.");
      Swal.fire({
        icon: "success",
        title: "Registration Successful",
        text: "Account created successfully! You can now log in.",
        customClass: { popup: "swal-popup-custom" }
      });
      setEmail("");
      setPassword("");
      setConfirmPass("");
      setMode("login");
      setLoading(false);
    } catch (err) {
      console.error("Registration error:", err);
      const errMsg = (err && err.message && !err.message.includes("fetch")) ? err.message : "Unable to connect to the server. Please check your internet connection and try again.";
      setError(errMsg);
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: errMsg,
        customClass: { popup: "swal-popup-custom" }
      });
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      <div style={styles.card}>

        {/* Logo */}
        <div style={styles.logoRow}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12
            }}
          >
            <div style={styles.logoBox}>C</div>

            <div>
              <div style={styles.logoTitle}>
                Customer Consent Portal
              </div>

              {/* <div style={styles.logoSub}>
        Data Subject Access Request
      </div> */}
            </div>
          </div>

          <button
            onClick={() => window.location.href = "/"}
            style={{
              position: "absolute",
              top: 16,
              right: 16,

              color: "#120202",
              fontSize: 18,
              fontWeight: 700,
              cursor: "pointer",
              zIndex: 1000
            }}
            title="Close"
          >
            ✕
          </button>

        </div>

        {/* Mode Toggle */}
        <div style={styles.toggleWrap}>
          <button
            onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
            style={{ ...styles.toggleBtn, ...(mode === "login" ? styles.toggleActive : {}) }}
          >
            🔐 Sign In
          </button>
          <button
            onClick={() => { setMode("register"); setError(""); setSuccess(""); }}
            style={{ ...styles.toggleBtn, ...(mode === "register" ? styles.toggleActive : {}) }}
          >
            ✎ Register
          </button>
        </div>

        {/* Description */}
        <div style={styles.desc}>
          {mode === "login"
            ? "Sign in with your registered email to view and manage your Customer Consent requests. You will only see records matching your email."
            : "Register a new account to access the Customer Consent Portal. You can then submit and manage your requests."}
        </div>

        {/* Form */}
        <form onSubmit={mode === "login" ? handleLogin : handleRegister} style={{ marginTop: 6 }}>
          {error && <div style={styles.errorBox}><span>⚠</span> {error}</div>}
          {success && <div style={styles.successBox}><span>✓</span> {success}</div>}

          <label style={styles.label}>Email Address</label>
          <input
            type="email" placeholder="you@example.com" value={email}
            onChange={e => setEmail(e.target.value)}
            required style={styles.input} autoComplete="email"
          />

          <label style={styles.label}>Password</label>
          <div style={{ position: "relative" }}>
            <input
              type={showPass ? "text" : "password"} placeholder="Enter password"
              value={password} onChange={e => setPassword(e.target.value)}
              required style={{ ...styles.input, paddingRight: 44 }}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            <button type="button" onClick={() => setShowPass(p => !p)} style={styles.eyeBtn} aria-label={showPass ? "Hide password" : "Show password"}>
              {showPass ? <EyeOff size={18} color="#64748b" /> : <Eye size={18} color="#64748b" />}
            </button>
          </div>

          {mode === "register" && (
            <>
              <label style={styles.label}>Confirm Password</label>
              <input
                type={showPass ? "text" : "password"} placeholder="Confirm password"
                value={confirmPass} onChange={e => setConfirmPass(e.target.value)}
                required style={styles.input} autoComplete="new-password"
              />
            </>
          )}

          <button type="submit" disabled={loading}
            style={{ ...styles.submitBtn, opacity: loading ? 0.75 : 1 }}>
            {loading
              ? (mode === "login" ? "Signing in…" : "Creating account…")
              : (mode === "login" ? "🔐 Sign In" : "✎ Create Account")}
          </button>
        </form>

      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100%",
    display: "flex", alignItems: "center", justifyContent: "center",
    // background: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 50%, #f8fafc 100%)",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: "relative", overflow: "hidden", padding: "24px 16px",
  },
  blob1: {
    position: "absolute", top: -100, right: -100, width: 400, height: 400,
    borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.15), transparent 70%)",
    pointerEvents: "none",
  },
  blob2: {
    position: "absolute", bottom: -120, left: -80, width: 360, height: 360,
    borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.1), transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    width: "100%", maxWidth: 440, background: "#ffffff", borderRadius: 24,
    padding: "36px 32px", boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
    position: "relative", zIndex: 1,
  },
  logoRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 24 },
  logoBox: { width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#fff", flexShrink: 0 },
  logoTitle: { fontSize: 18, fontWeight: 700, color: "#111827", letterSpacing: "-0.4px" },
  logoSub: { fontSize: 11, color: "#9ca3af", marginTop: 2 },
  toggleWrap: { display: "flex", background: "#f1f5f9", borderRadius: 12, padding: 4, marginBottom: 16, gap: 4 },
  toggleBtn: { flex: 1, padding: "9px 8px", border: "none", borderRadius: 9, background: "transparent", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" },
  toggleActive: { background: "#ffffff", color: "#2563eb", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
  desc: { fontSize: 12.5, color: "#6b7280", marginBottom: 20, lineHeight: 1.6, background: "#f8fafc", borderRadius: 8, padding: "10px 12px", border: "1px solid #e2e8f0" },
  label: { fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 },
  input: { width: "100%", height: 48, marginBottom: 16, borderRadius: 12, border: "1.5px solid #e2e8f0", padding: "0 14px", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s", fontFamily: "inherit" },
  eyeBtn: { position: "absolute", right: 12, top: "50%", transform: "translateY(-65%)", background: "none", border: "none", outline: "none", boxShadow: "none", cursor: "pointer", fontSize: 16 },
  errorBox: { background: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 7 },
  successBox: { background: "#f0fdf4", color: "#166534", border: "1px solid #86efac", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 7 },
  submitBtn: { width: "100%", height: 52, border: "none", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 700, background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", marginTop: 4, boxShadow: "0 4px 16px rgba(37,99,235,0.35)", transition: "opacity 0.2s" },
  accessBox: { marginTop: 20, background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #e2e8f0" },
  accessTitle: { fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 },
  accessList: { margin: 0, paddingLeft: 0, listStyle: "none", fontSize: 12, color: "#6b7280", lineHeight: 2 },
};

export default DSARLogin;